<template>
  <div class="flow-manager-entry">
    <section class="flow-manager-launcher">
      <div class="flow-manager-launcher-icon">
        <Icon icon="tabler:route-square-2" width="27" height="27" />
      </div>
      <div class="flow-manager-launcher-copy">
        <span>Conversation flow</span>
        <h2>Build a clear journey for every conversation</h2>
        <p>Add steps, decisions, tools, and phone actions in a guided visual workspace.</p>
        <div class="flow-manager-launcher-stats">
          <span><b>{{ nodes.length }}</b> steps</span>
          <span><b>{{ edges.length }}</b> connections</span>
          <span :class="{ 'flow-manager-stat-warning': issues.length > 0 }"><b>{{ issues.length }}</b> items to review</span>
        </div>
      </div>
      <button type="button" class="btn" @click="openFlowBuilder">
        <Icon icon="tabler:edit" width="17" height="17" />
        Open FlowManager
      </button>
    </section>

    <AppModal
      :visible="isOpen === true"
      title="FlowManager"
      box-class="flow-manager-modal-box"
      :show-close-button="false"
      @close="closeFlowBuilder"
    >
      <div class="flow-manager-shell" tabindex="0" @keydown="handleKeyDown">
        <header class="flow-manager-toolbar">
          <div class="flow-manager-toolbar-title">
            <span class="flow-manager-toolbar-logo">
              <Icon icon="tabler:route-square-2" width="20" height="20" />
            </span>
            <div>
              <strong>Conversation flow</strong>
              <small>{{ nodes.length }} steps · {{ edges.length }} connections</small>
            </div>
          </div>
          <div class="flow-manager-toolbar-actions">
            <label class="flow-manager-auto">
              <input v-model="autoArrangeEnabled" type="checkbox" class="toggle toggle-sm" @change="handleAutoArrangeChange" />
              <span>Auto arrange</span>
            </label>
            <button type="button" class="btn btn-sm" :disabled="canUndo === false" title="Undo" @click="undo">
              <Icon icon="tabler:arrow-back-up" width="17" height="17" />
            </button>
            <button type="button" class="btn btn-sm" :disabled="canRedo === false" title="Redo" @click="redo">
              <Icon icon="tabler:arrow-forward-up" width="17" height="17" />
            </button>
            <button type="button" class="btn btn-sm" title="Fit flow to screen" @click="centerCanvas">
              <Icon icon="tabler:focus-centered" width="17" height="17" />
              Fit
            </button>
            <button type="button" class="btn btn-sm" title="Arrange all steps" @click="organizeFlow">
              <Icon icon="tabler:hierarchy-2" width="17" height="17" />
              Arrange
            </button>
            <button type="button" class="btn btn-sm" :class="{ 'flow-manager-review-active': showIssues === true }" @click="showIssues = showIssues !== true">
              <Icon icon="tabler:shield-check" width="17" height="17" />
              Review
              <span v-if="issues.length > 0" class="badge badge-warning badge-sm">{{ issues.length }}</span>
            </button>
            <button type="button" class="btn btn-sm" :class="{ 'flow-manager-adi-active': assistantOpen === true }" @click="assistantOpen = assistantOpen !== true">
              <Icon icon="tabler:sparkles" width="17" height="17" />
              Adi
            </button>
            <button type="button" class="btn btn-sm" @click="closeFlowBuilder">
              <Icon icon="tabler:x" width="17" height="17" />
              Close
            </button>
          </div>
        </header>

        <main class="flow-manager-workspace">
          <FlowManagerLibrary
            :definitions="nodeDefinitions"
            @add-node="addNode"
            @drag-node="draggedDefinition = $event"
          />

          <section class="flow-manager-stage" @dragover.prevent @drop="handleDrop">
            <VueFlow
              :key="flowKey"
              v-model="elements"
              class="flow-manager-canvas"
              :node-types="nodeTypes"
              :fit-view-on-init="true"
              :min-zoom="0.2"
              :max-zoom="2.5"
              :default-zoom="0.85"
              :delete-key-code="null"
              :default-edge-options="defaultEdgeOptions"
              :multi-selection-key-code="multiSelectionKey"
              @connect="connectNodes"
              @node-click="selectNode"
              @node-double-click="selectNode"
              @edge-click="selectEdge"
              @edge-double-click="selectEdge"
              @pane-click="clearSelection"
              @node-drag-stop="recordCurrentState"
            >
              <Background variant="lines" pattern-color="#dbe4ef" :line-width="1" />
              <Controls position="bottom-left" />
            </VueFlow>

            <div class="flow-manager-canvas-help">
              <Icon icon="tabler:info-circle" width="15" height="15" />
              Drag the canvas to move around. Drag between dots to connect steps.
            </div>

            <transition name="flow-manager-pop">
              <section v-if="showIssues === true" class="flow-manager-review">
                <header>
                  <div>
                    <span>Flow review</span>
                    <h3>{{ issues.length === 0 ? 'Everything looks ready' : `${issues.length} items need attention` }}</h3>
                  </div>
                  <button type="button" class="btn btn-sm btn-ghost btn-circle" @click="showIssues = false">
                    <Icon icon="tabler:x" width="16" height="16" />
                  </button>
                </header>
                <div v-if="issues.length === 0" class="flow-manager-review-ready">
                  <Icon icon="tabler:circle-check" width="30" height="30" />
                  <p>Every step is connected and the required settings are filled in.</p>
                </div>
                <button v-for="issue in issues" v-else :key="issue.id" type="button" class="flow-manager-issue" @click="focusIssue(issue)">
                  <Icon icon="tabler:alert-triangle" width="16" height="16" />
                  <span>{{ issue.text }}</span>
                  <Icon icon="tabler:chevron-right" width="15" height="15" />
                </button>
              </section>
            </transition>

            <FlowManagerAssistant
              :open="assistantOpen"
              :agent-id="agentId"
              :current-flow="serializedFlow"
              :selected-channels="selectedChannels"
              @close="assistantOpen = false"
              @apply-flow="applyAssistantFlow"
            />
          </section>

          <FlowManagerInspector
            :selection="selection"
            :tools="availableTools"
            :nodes="nodes"
            :selected-channels="selectedChannels"
            @close="clearSelection"
            @save-node="saveNode"
            @save-edge="saveEdge"
            @delete-selection="requestDeleteSelection"
          />
        </main>
      </div>
    </AppModal>

    <Teleport to="body">
      <dialog :open="deleteDialogOpen === true" class="modal flow-manager-delete-modal" :class="{ 'modal-open': deleteDialogOpen === true }">
        <div class="modal-box max-w-md">
          <div class="flow-manager-delete-icon">
            <Icon icon="tabler:trash" width="24" height="24" />
          </div>
          <h3 class="font-bold text-lg">Delete {{ selection !== null && selection.kind === 'edge' ? 'this connection' : 'this step' }}?</h3>
          <p class="py-3 text-sm text-base-content/70">This change can be undone from the FlowManager toolbar.</p>
          <div class="modal-action">
            <button type="button" class="btn" @click="deleteDialogOpen = false">Cancel</button>
            <button type="button" class="btn btn-error" @click="confirmDeleteSelection">Delete</button>
          </div>
        </div>
        <form method="dialog" class="modal-backdrop">
          <button type="button" @click="deleteDialogOpen = false">Close</button>
        </form>
      </dialog>
    </Teleport>
  </div>
</template>

<script setup>
import { computed, nextTick, onMounted, ref, shallowRef, watch } from 'vue';
import { Icon } from '@iconify/vue/dist/offline';
import { VueFlow, MarkerType, useVueFlow } from '@vue-flow/core';
import { Background } from '@vue-flow/background';
import { Controls } from '@vue-flow/controls';
import AppModal from '@/components/AppModal.vue';
import axios from '@/api/axiosInstance';
import { globalState } from '@/globalState';
import FlowManagerLibrary from './FlowManagerLibrary.vue';
import FlowManagerInspector from './FlowManagerInspector.vue';
import FlowManagerAssistant from './FlowManagerAssistant.vue';
import FlowManagerNode from './FlowManagerNode.vue';
import {
  autoLayout,
  deepCopy,
  getNodeDefinitions,
  getStartingMessageText,
  hasText,
  normalizeFlow,
  serializeFlow,
  validateFlow
} from './flowManagerModel';

import '@vue-flow/core/dist/style.css';
import '@vue-flow/core/dist/theme-default.css';
import '@vue-flow/controls/dist/style.css';

const props = defineProps({
  startingMessage: {
    type: [String, Object],
    default: ''
  },
  agentId: {
    type: [String, Number],
    default: ''
  },
  selectedChannels: {
    type: Array,
    default: () => []
  }
});

const emit = defineEmits(['builder-closed', 'update:startingMessage']);
const { fitView, project } = useVueFlow();
const elements = ref([]);
const availableTools = ref([]);
const isOpen = ref(false);
const assistantOpen = ref(false);
const autoArrangeEnabled = ref(true);
const showIssues = ref(false);
const selection = ref(null);
const draggedDefinition = ref(null);
const deleteDialogOpen = ref(false);
const flowKey = ref(0);
const history = ref([]);
const historyIndex = ref(-1);
let nextNodeNumber = 1;

const nodeTypes = shallowRef({
  Root: FlowManagerNode,
  Question: FlowManagerNode,
  KnowledgeBase: FlowManagerNode,
  If: FlowManagerNode,
  Action: FlowManagerNode
});

const defaultEdgeOptions = {
  type: 'smoothstep',
  animated: false,
  markerEnd: {
    type: MarkerType.ArrowClosed,
    width: 20,
    height: 20,
    color: '#94a3b8'
  },
  style: {
    stroke: '#94a3b8',
    strokeWidth: 1.6
  }
};

const nodes = computed(() => elements.value.filter(element => element.position !== undefined && element.position !== null));
const edges = computed(() => elements.value.filter(element => element.source !== undefined && element.target !== undefined));
const rawIssues = computed(() => validateFlow(elements.value));
const issues = computed(() => rawIssues.value);
const nodeDefinitions = computed(() => getNodeDefinitions(availableTools.value, props.selectedChannels));
const serializedFlow = computed(() => serializeFlow(elements.value, props.startingMessage));
const canUndo = computed(() => historyIndex.value > 0);
const canRedo = computed(() => historyIndex.value >= 0 && historyIndex.value < history.value.length - 1);

watch(rawIssues, currentIssues => {
  const issueCounts = new Map();
  for (let index = 0; index < currentIssues.length; index = index + 1) {
    const issue = currentIssues[index];
    issueCounts.set(issue.nodeId, issueCounts.has(issue.nodeId) === true ? issueCounts.get(issue.nodeId) + 1 : 1);
  }
  let changed = false;
  const updated = elements.value.map(element => {
    if (element.position === undefined) {
      return element;
    }
    if (element.position === null) {
      return element;
    }
    const count = issueCounts.has(element.id) === true ? issueCounts.get(element.id) : 0;
    const oldCount = Number(element.data.flowManagerIssueCount);
    if (oldCount === count) {
      return element;
    }
    changed = true;
    return {
      ...element,
      data: {
        ...element.data,
        flowManagerIssueCount: count
      }
    };
  });
  if (changed === true) {
    elements.value = updated;
  }
}, { immediate: true });

watch(() => props.startingMessage, startingMessage => {
  const root = nodes.value.find(node => node.data.nodeType === 'Root');
  if (root === undefined) {
    return;
  }
  const nextMessage = getStartingMessageText(startingMessage);
  if (nextMessage.length === 0) {
    return;
  }
  if (hasText(root.data.message) === true) {
    return;
  }
  root.data.message = nextMessage;
}, { deep: true });

onMounted(async () => {
  loadInternalFlow([]);
  await fetchTools();
});

async function fetchTools() {
  try {
    const response = await axios.get(`${globalState.apiEndpoint}/api/tools/getAllAITools`);
    availableTools.value = Array.isArray(response.data) === true ? response.data : [];
  } catch (error) {
    availableTools.value = [];
  }
}

function multiSelectionKey(event) {
  if (event === null) {
    return false;
  }
  if (event === undefined) {
    return false;
  }
  return [event.ctrlKey === true, event.metaKey === true, event.shiftKey === true].includes(true);
}

function makeNode(definition, position) {
  const data = deepCopy(definition.data);
  data.flowManagerIcon = definition.icon;
  data.flowManagerIsConnectedTool = definition.isConnectedTool === true;
  const node = {
    id: `step-${nextNodeNumber}`,
    type: definition.type,
    position,
    label: data.label,
    data,
    draggable: true
  };
  nextNodeNumber = nextNodeNumber + 1;
  return node;
}

function getNewNodePosition() {
  const count = nodes.value.length;
  return {
    x: 100 + count * 70,
    y: 80 + count * 45
  };
}

function addNode(definition) {
  const nextElements = [...elements.value, makeNode(definition, getNewNodePosition())];
  applyStructuralChange(nextElements);
}

function handleDrop(event) {
  event.preventDefault();
  if (draggedDefinition.value === null) {
    return;
  }
  const stage = event.currentTarget.getBoundingClientRect();
  const position = project({
    x: event.clientX - stage.left,
    y: event.clientY - stage.top
  });
  const nextElements = [...elements.value, makeNode(draggedDefinition.value, position)];
  draggedDefinition.value = null;
  applyStructuralChange(nextElements);
}

function connectNodes(connection) {
  const source = nodes.value.find(node => node.id === connection.source);
  if (source === undefined) {
    return;
  }
  if (source.data.actionType === 'endCall') {
    return;
  }
  const duplicate = edges.value.some(edge => edge.source === connection.source && edge.target === connection.target);
  if (duplicate === true) {
    return;
  }
  const edge = {
    id: `edge-${connection.source}-${connection.target}-${Date.now()}`,
    source: connection.source,
    target: connection.target,
    type: 'smoothstep',
    label: '',
    data: { ifCondition: '' },
    ...defaultEdgeOptions
  };
  applyStructuralChange([...elements.value, edge]);
}

function applyStructuralChange(nextElements) {
  const normalized = normalizeFlow(nextElements, props.startingMessage);
  elements.value = autoArrangeEnabled.value === true ? autoLayout(normalized) : normalized;
  refreshSelection();
  recordCurrentState();
  nextTick(() => centerCanvas());
}

function organizeFlow() {
  elements.value = autoLayout(normalizeFlow(elements.value, props.startingMessage));
  recordCurrentState();
  nextTick(() => centerCanvas());
}

function handleAutoArrangeChange() {
  if (autoArrangeEnabled.value === true) {
    organizeFlow();
  }
}

function selectNode(event) {
  if (event === null) {
    return;
  }
  if (event === undefined) {
    return;
  }
  if (event.node === undefined) {
    return;
  }
  selection.value = { kind: 'node', id: event.node.id, element: event.node };
}

function selectEdge(event) {
  if (event === null) {
    return;
  }
  if (event === undefined) {
    return;
  }
  if (event.edge === undefined) {
    return;
  }
  if (event.event !== null && event.event !== undefined) {
    event.event.stopPropagation();
  }
  selection.value = { kind: 'edge', id: event.edge.id, element: event.edge };
}

function clearSelection() {
  selection.value = null;
}

function refreshSelection() {
  if (selection.value === null) {
    return;
  }
  const current = elements.value.find(element => element.id === selection.value.id);
  if (current === undefined) {
    clearSelection();
    return;
  }
  selection.value = { ...selection.value, element: current };
}

function saveNode(updatedData) {
  if (selection.value === null) {
    return;
  }
  if (selection.value.kind !== 'node') {
    return;
  }
  elements.value = elements.value.map(element => {
    if (element.id !== selection.value.id) {
      return element;
    }
    return {
      ...element,
      type: updatedData.nodeType,
      label: updatedData.label,
      data: {
        ...updatedData,
        flowManagerIssueCount: element.data.flowManagerIssueCount
      }
    };
  });
  if (updatedData.nodeType === 'Root') {
    emit('update:startingMessage', buildStartingMessagePayload(updatedData.message));
  }
  refreshSelection();
  recordCurrentState();
}

function saveEdge(condition) {
  if (selection.value === null) {
    return;
  }
  if (selection.value.kind !== 'edge') {
    return;
  }
  elements.value = elements.value.map(element => {
    if (element.id !== selection.value.id) {
      return element;
    }
    return {
      ...element,
      label: condition,
      data: {
        ...(element.data !== null && element.data !== undefined ? element.data : {}),
        ifCondition: condition
      }
    };
  });
  refreshSelection();
  recordCurrentState();
}

function buildStartingMessagePayload(message) {
  if (props.startingMessage !== null && props.startingMessage !== undefined && typeof props.startingMessage === 'object' && Array.isArray(props.startingMessage) === false) {
    return { ...props.startingMessage, default: message };
  }
  return { default: message };
}

function requestDeleteSelection() {
  if (selection.value === null) {
    return;
  }
  if (selection.value.kind === 'node' && selection.value.element.data.nodeType === 'Root') {
    return;
  }
  deleteDialogOpen.value = true;
}

function confirmDeleteSelection() {
  if (selection.value === null) {
    deleteDialogOpen.value = false;
    return;
  }
  const id = selection.value.id;
  let nextElements = elements.value.filter(element => element.id !== id);
  if (selection.value.kind === 'node') {
    nextElements = nextElements.filter(element => element.source !== id && element.target !== id);
  }
  deleteDialogOpen.value = false;
  clearSelection();
  applyStructuralChange(nextElements);
}

function focusIssue(issue) {
  const node = nodes.value.find(item => item.id === issue.nodeId);
  if (node === undefined) {
    return;
  }
  selection.value = { kind: 'node', id: node.id, element: node };
  showIssues.value = false;
}

function centerCanvas() {
  nextTick(() => fitView({ padding: 0.2, maxZoom: 1 }));
}

function getHistorySnapshot() {
  const clean = serializeFlow(elements.value, props.startingMessage);
  for (let index = 0; index < clean.length; index = index + 1) {
    if (clean[index].data !== null && clean[index].data !== undefined) {
      delete clean[index].data.flowManagerIssueCount;
    }
  }
  return JSON.stringify(clean);
}

function recordCurrentState() {
  const snapshot = getHistorySnapshot();
  if (historyIndex.value >= 0 && history.value[historyIndex.value] === snapshot) {
    return;
  }
  history.value = history.value.slice(0, historyIndex.value + 1);
  history.value.push(snapshot);
  if (history.value.length > 60) {
    history.value.shift();
  }
  historyIndex.value = history.value.length - 1;
}

function restoreHistory(index) {
  if (index < 0) {
    return;
  }
  if (index >= history.value.length) {
    return;
  }
  historyIndex.value = index;
  elements.value = normalizeFlow(JSON.parse(history.value[index]), props.startingMessage);
  clearSelection();
  flowKey.value = flowKey.value + 1;
  nextTick(() => centerCanvas());
}

function undo() {
  if (canUndo.value === true) {
    restoreHistory(historyIndex.value - 1);
  }
}

function redo() {
  if (canRedo.value === true) {
    restoreHistory(historyIndex.value + 1);
  }
}

function handleKeyDown(event) {
  const target = event.target;
  const tagName = target !== null && target !== undefined && target.tagName !== undefined ? target.tagName.toLowerCase() : '';
  const isInput = ['input', 'textarea', 'select'].includes(tagName) === true;
  if (isInput === true) {
    return;
  }
  if (event.key === 'Escape') {
    if (assistantOpen.value === true) {
      assistantOpen.value = false;
      return;
    }
    if (selection.value !== null) {
      clearSelection();
      return;
    }
    closeFlowBuilder();
    return;
  }
  if (['Delete', 'Backspace'].includes(event.key) === true && selection.value !== null) {
    requestDeleteSelection();
    return;
  }
  if ([event.ctrlKey === true, event.metaKey === true].includes(true) && event.key.toLowerCase() === 'z') {
    event.preventDefault();
    if (event.shiftKey === true) {
      redo();
    } else {
      undo();
    }
  }
}

function loadInternalFlow(flowData) {
  let normalized = normalizeFlow(flowData, props.startingMessage);
  if (autoArrangeEnabled.value === true) {
    normalized = autoLayout(normalized);
  }
  elements.value = normalized;
  updateNextNodeNumber();
  clearSelection();
  history.value = [];
  historyIndex.value = -1;
  recordCurrentState();
  flowKey.value = flowKey.value + 1;
  nextTick(() => centerCanvas());
}

function updateNextNodeNumber() {
  let maximum = 0;
  for (let index = 0; index < nodes.value.length; index = index + 1) {
    const match = String(nodes.value[index].id).match(/^step-(\d+)$/);
    if (match !== null && match[1] !== undefined) {
      const number = Number.parseInt(match[1], 10);
      if (Number.isFinite(number) === true && number > maximum) {
        maximum = number;
      }
    }
  }
  nextNodeNumber = maximum + 1;
}

function applyAssistantFlow(flowData) {
  loadInternalFlow(flowData);
  organizeFlow();
}

function getFlowData() {
  return serializeFlow(elements.value, props.startingMessage);
}

function loadFlowData(flowData) {
  loadInternalFlow(flowData);
}

function openFlowBuilder() {
  isOpen.value = true;
  nextTick(() => centerCanvas());
}

function closeFlowBuilder() {
  isOpen.value = false;
  assistantOpen.value = false;
  showIssues.value = false;
  clearSelection();
  emit('builder-closed');
}

defineExpose({
  getFlowData,
  loadFlowData,
  fitView,
  openFlowBuilder,
  closeFlowBuilder
});
</script>

<style scoped>
.flow-manager-entry {
  width: 100%;
  color: #172033;
}

.flow-manager-launcher {
  display: flex;
  align-items: center;
  min-height: 8rem;
  padding: 1.25rem 1.4rem;
  border: 1px solid #dfe5ec;
  border-radius: 0.55rem;
  background: #ffffff;
  box-shadow: none;
}

.flow-manager-launcher-icon {
  width: 3.2rem;
  height: 3.2rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 1px solid #cdd9e6;
  border-radius: 0.5rem;
  background: #f2f6fa;
  color: #345e8a;
  flex-shrink: 0;
  box-shadow: none;
}

.flow-manager-launcher-copy {
  min-width: 0;
  margin-left: 1rem;
}

.flow-manager-launcher-copy > span {
  color: #586b81;
  font-size: 0.62rem;
  font-weight: 650;
  letter-spacing: 0.07em;
  text-transform: uppercase;
}

.flow-manager-launcher-copy h2 {
  margin: 0.2rem 0 0;
  color: #18283a;
  font-size: 1.08rem;
  font-weight: 650;
  letter-spacing: -0.01em;
}

.flow-manager-launcher-copy p {
  margin: 0.3rem 0 0;
  color: #68788b;
  font-size: 0.75rem;
}

.flow-manager-launcher-stats {
  display: flex;
  align-items: center;
  margin-top: 0.65rem;
}

.flow-manager-launcher-stats span {
  color: #68788b;
  font-size: 0.65rem;
  font-weight: 500;
}

.flow-manager-launcher-stats span + span {
  margin-left: 0.9rem;
}

.flow-manager-launcher-stats b {
  color: #2f4156;
  font-size: 0.68rem;
  font-weight: 700;
}

.flow-manager-launcher-stats .flow-manager-stat-warning {
  color: #a05912;
}

.flow-manager-launcher-stats .flow-manager-stat-warning b {
  color: #a05912;
}

.flow-manager-launcher > .btn {
  margin-left: auto;
  flex-shrink: 0;
}

.flow-manager-launcher > .btn svg {
  margin-right: 0.4rem;
}

.flow-manager-shell {
  display: flex;
  flex-direction: column;
  height: calc(100dvh - 2rem);
  min-height: 0;
  overflow: hidden;
  outline: none;
  background: #f4f6f8;
}

.flow-manager-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  min-height: 3.6rem;
  padding: 0.55rem 0.7rem;
  border-bottom: 1px solid #dbe3ee;
  background: #ffffff;
}

.flow-manager-toolbar-title {
  display: flex;
  align-items: center;
  min-width: 0;
}

.flow-manager-toolbar-logo {
  width: 2.25rem;
  height: 2.25rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 1px solid #d7e0ea;
  border-radius: 0.45rem;
  background: #f3f6f9;
  color: #345e8a;
  flex-shrink: 0;
}

.flow-manager-toolbar-title > div {
  display: flex;
  flex-direction: column;
  min-width: 0;
  margin-left: 0.6rem;
}

.flow-manager-toolbar-title strong {
  color: #18283a;
  font-size: 0.82rem;
  font-weight: 700;
}

.flow-manager-toolbar-title small {
  margin-top: 0.1rem;
  color: #64748b;
  font-size: 0.62rem;
}

.flow-manager-toolbar-actions {
  display: flex;
  align-items: center;
  margin-left: 1rem;
}

.flow-manager-toolbar-actions > * + * {
  margin-left: 0.35rem;
}

.flow-manager-toolbar-actions .btn svg {
  margin-right: 0.25rem;
}

.flow-manager-toolbar-actions .btn-circle svg {
  margin-right: 0;
}

.flow-manager-auto {
  display: inline-flex;
  align-items: center;
  margin-right: 0.25rem;
  color: #475569;
  font-size: 0.68rem;
  font-weight: 650;
}

.flow-manager-auto span {
  margin-left: 0.4rem;
}

.flow-manager-adi-active,
.flow-manager-review-active {
  background: #172033;
  color: #ffffff;
}

.flow-manager-workspace {
  display: grid;
  grid-template-columns: 250px minmax(0, 1fr) 370px;
  flex: 1;
  min-height: 0;
  direction: ltr;
}

.flow-manager-stage {
  position: relative;
  min-width: 0;
  min-height: 0;
  overflow: hidden;
  background: #f6f8fa;
}

.flow-manager-canvas {
  width: 100%;
  height: 100%;
  background: #f8fafb;
}

.flow-manager-canvas-help {
  position: absolute;
  left: 1rem;
  top: 1rem;
  display: inline-flex;
  align-items: center;
  padding: 0.45rem 0.6rem;
  border: 1px solid #dde4eb;
  border-radius: 0.35rem;
  background: rgba(255, 255, 255, 0.92);
  color: #52637a;
  box-shadow: 0 2px 8px rgba(15, 23, 42, 0.04);
  font-size: 0.63rem;
  pointer-events: none;
}

.flow-manager-canvas-help svg {
  margin-right: 0.35rem;
}

.flow-manager-review {
  position: absolute;
  top: 0.75rem;
  right: 0.75rem;
  width: min(360px, calc(100% - 1.5rem));
  max-height: calc(100% - 1.5rem);
  overflow-y: auto;
  z-index: 30;
  border: 1px solid #cfd9e6;
  border-radius: 0.65rem;
  background: #ffffff;
  box-shadow: 0 18px 48px rgba(15, 23, 42, 0.18);
}

.flow-manager-review header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.8rem;
  border-bottom: 1px solid #edf1f6;
}

.flow-manager-review header span {
  color: #64748b;
  font-size: 0.62rem;
  font-weight: 700;
  letter-spacing: 0.05em;
  text-transform: uppercase;
}

.flow-manager-review header h3 {
  margin: 0.15rem 0 0;
  color: #172033;
  font-size: 0.8rem;
  font-weight: 700;
}

.flow-manager-issue {
  display: flex;
  align-items: center;
  width: 100%;
  padding: 0.7rem 0.8rem;
  border: 0;
  border-bottom: 1px solid #edf1f6;
  background: #ffffff;
  color: #475569;
  text-align: left;
}

.flow-manager-issue:hover {
  background: #f8fafc;
}

.flow-manager-issue > svg:first-child {
  color: #d97706;
  flex-shrink: 0;
}

.flow-manager-issue span {
  flex: 1;
  margin: 0 0.5rem;
  font-size: 0.68rem;
  line-height: 1.4;
}

.flow-manager-review-ready {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 1.5rem;
  color: #059669;
  text-align: center;
}

.flow-manager-review-ready p {
  margin: 0.6rem 0 0;
  color: #475569;
  font-size: 0.7rem;
  line-height: 1.45;
}

.flow-manager-pop-enter-active,
.flow-manager-pop-leave-active {
  transition: opacity 0.15s ease, transform 0.15s ease;
}

.flow-manager-pop-enter-from,
.flow-manager-pop-leave-to {
  opacity: 0;
  transform: translateY(-0.4rem);
}

:deep(.vue-flow__node) {
  border: 0;
  padding: 0;
  border-radius: 0.8rem;
  background: transparent;
  box-shadow: none;
}

:deep(.vue-flow__node.selected .flow-manager-node) {
  box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.16), 0 14px 30px rgba(15, 23, 42, 0.12);
  border-color: #2f72c7;
}

:deep(.vue-flow__edge.selected .vue-flow__edge-path) {
  stroke: #2563eb;
  stroke-width: 2.5;
}

:deep(.vue-flow__edge-text) {
  font-size: 10px;
  font-weight: 650;
  fill: #334155;
}

:deep(.vue-flow__edge-textbg) {
  fill: #ffffff;
  stroke: #dbe3ee;
  stroke-width: 1px;
}

:deep(.vue-flow__controls) {
  overflow: hidden;
  border: 1px solid #cfd9e6;
  border-radius: 0.45rem;
  box-shadow: 0 7px 22px rgba(15, 23, 42, 0.09);
}

:deep(.vue-flow__controls-button) {
  border-bottom-color: #e2e8f0;
  background: #ffffff;
  color: #3d4f68;
}

:deep(.vue-flow__controls-button:hover) {
  background: #eef4fb;
}

.flow-manager-delete-icon {
  width: 2.8rem;
  height: 2.8rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 0.8rem;
  border-radius: 0.8rem;
  background: #fef2f2;
  color: #dc2626;
}

@media (max-width: 1250px) {
  .flow-manager-workspace {
    grid-template-columns: 220px minmax(0, 1fr) 340px;
  }

  .flow-manager-toolbar-actions .btn {
    padding-left: 0.55rem;
    padding-right: 0.55rem;
  }
}

@media (max-width: 980px) {
  .flow-manager-workspace {
    grid-template-columns: 205px minmax(0, 1fr);
  }

  .flow-manager-workspace > :last-child {
    position: absolute;
    top: 3.6rem;
    right: 0;
    bottom: 0;
    width: min(355px, 90vw);
    z-index: 25;
    box-shadow: -12px 0 35px rgba(15, 23, 42, 0.12);
  }

  .flow-manager-toolbar-title {
    display: none;
  }
}
</style>

<style>
.flow-manager-modal-box {
  width: calc(100vw - 1rem) !important;
  max-width: none !important;
  height: calc(100dvh - 1rem) !important;
  max-height: none !important;
  padding: 0 !important;
  border-radius: 0.9rem !important;
}

.flow-manager-modal-box > .app-modal-header-new {
  display: none;
}

.flow-manager-modal-box > .app-modal-content {
  height: 100%;
  overflow: hidden;
}

.flow-manager-delete-modal {
  z-index: 12000;
}
</style>
